import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { matchesAPI, teamsAPI } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import toast from 'react-hot-toast';

function UploadPage() {
  const navigate = useNavigate();
  const { user, currentTeam } = useAuth();
  const [loading, setLoading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [processingProgress, setProcessingProgress] = useState(0);
  const [processingPhase, setProcessingPhase] = useState(''); // 'uploading', 'processing', 'completed'
  const [matchId, setMatchId] = useState(null);
  const [teams, setTeams] = useState([]);
  
  // Form state
  const [selectedTeamId, setSelectedTeamId] = useState('');
  const [title, setTitle] = useState('');
  const [homeTeam, setHomeTeam] = useState('');
  const [awayTeam, setAwayTeam] = useState('');
  const [file, setFile] = useState(null);

  // Load teams on mount
  useEffect(() => {
    const loadTeams = async () => {
      try {
        const data = await teamsAPI.getMyTeams();
        setTeams(data);

        // Pre-select current team or first team
        if (currentTeam) {
          setSelectedTeamId(currentTeam.id.toString());
        } else if (data.length > 0) {
          setSelectedTeamId(data[0].id.toString());
        }
      } catch (error) {
        console.error('Error loading teams:', error);
      }
    };
    loadTeams();
  }, [currentTeam]);

  // Poll for processing progress
  useEffect(() => {
    if (!matchId || processingProgress >= 100) return;

    const pollInterval = setInterval(async () => {
      try {
        const match = await matchesAPI.getMatchProgress(matchId);
        setProcessingProgress(match.progress || 0);

        if (match.status === 'review') {
          setProcessingPhase('completed');
          setProcessingProgress(100);
          clearInterval(pollInterval);
          toast.success('✅ Processing complete! Ready for review.');
          setTimeout(() => navigate('/matches'), 2000);
        } else if (match.status === 'error') {
          clearInterval(pollInterval);
          toast.error('❌ Processing failed. Please try again.');
          setProcessingPhase('');
          setLoading(false);
        } else if (match.status === 'processing') {
          setProcessingPhase('processing');
        }
      } catch (error) {
        console.error('Error polling progress:', error);
      }
    }, 2000); // Poll every 2 seconds

    return () => clearInterval(pollInterval);
  }, [matchId, processingProgress, navigate]);

  const handleUpload = async (e) => {
    e.preventDefault();

    if (!selectedTeamId) {
      toast.error('Please select a team');
      return;
    }

    if (!file) {
      toast.error('Please select a video file');
      return;
    }

    // Validate file size before upload
    const fileSizeMB = file.size / (1024 * 1024);
    if (fileSizeMB > 2048) {
      toast.error(`File too large: ${fileSizeMB.toFixed(2)} MB. Maximum size is 2 GB.`);
      return;
    }
    if (fileSizeMB > 500) {
      toast(`⚠️ Large file detected (${fileSizeMB.toFixed(2)} MB). Upload may take several minutes...`, {
        duration: 5000,
        icon: '⏳'
      });
    }

    setLoading(true);
    setUploadProgress(0);
    setProcessingProgress(0);
    setProcessingPhase('uploading');

    console.log('[UploadPage] Starting upload...', {
      teamId: selectedTeamId,
      title: title || 'Untitled Match',
      fileName: file.name,
      fileSize: `${fileSizeMB.toFixed(2)} MB`
    });

    try {
      const result = await matchesAPI.uploadVideo(
        parseInt(selectedTeamId),
        title || 'Untitled Match',
        file,
        homeTeam || null,
        awayTeam || null,
        (progress) => {
          setUploadProgress(progress);
          console.log(`[UploadPage] Upload progress: ${progress}%`);
        }
      );

      console.log('[UploadPage] Upload successful:', result);
      toast.success('📹 Video uploaded! Starting GPU processing on Modal...');

      // Set match ID to start polling
      setMatchId(result.id);
      setProcessingPhase('processing');
      setProcessingProgress(5);
    } catch (err) {
      console.error('[UploadPage] Upload error:', err);
      
      // Extract error message
      let errorMessage = 'Upload failed';
      if (err.message) {
        errorMessage = err.message;
      } else if (err.response?.data?.detail) {
        errorMessage = err.response.data.detail;
      } else if (err.response?.data?.message) {
        errorMessage = err.response.data.message;
      }
      
      toast.error(`❌ ${errorMessage}`, { duration: 6000 });
      setLoading(false);
      setProcessingPhase('');
      setUploadProgress(0);
    }
  };

  if (teams.length === 0) {
    return (
      <div style={styles.container}>
        <div style={styles.card}>
          <div style={styles.emptyState}>
            <span style={{ fontSize: '48px' }}>🏐</span>
            <h2>No Teams Yet</h2>
            <p>You need to create or join a team before uploading videos.</p>
            <button 
              onClick={() => navigate('/teams')}
              style={styles.primaryButton}
            >
              Go to Teams
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div style={styles.container}>
      <h1 style={styles.title}>Upload Match Video</h1>

      <div style={styles.card}>
        <form onSubmit={handleUpload}>
          {/* Team Selection */}
          <div style={styles.formGroup}>
            <label style={styles.label}>Team *</label>
            <select
              style={styles.select}
              value={selectedTeamId}
              onChange={(e) => setSelectedTeamId(e.target.value)}
              required
            >
              <option value="">Select a team...</option>
              {teams.map(team => (
                <option key={team.id} value={team.id}>
                  {team.name}
                </option>
              ))}
            </select>
          </div>

          {/* Match Title */}
          <div style={styles.formGroup}>
            <label style={styles.label}>Match Title *</label>
            <input
              type="text"
              style={styles.input}
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="e.g., Eagles vs. Warriors - Championship"
              required
            />
          </div>

          {/* Teams */}
          <div style={styles.row}>
            <div style={styles.formGroup}>
              <label style={styles.label}>Home Team</label>
              <input
                type="text"
                style={styles.input}
                value={homeTeam}
                onChange={(e) => setHomeTeam(e.target.value)}
                placeholder="Team name"
              />
            </div>
            <div style={styles.formGroup}>
              <label style={styles.label}>Away Team</label>
              <input
                type="text"
                style={styles.input}
                value={awayTeam}
                onChange={(e) => setAwayTeam(e.target.value)}
                placeholder="Team name"
              />
            </div>
          </div>

          {/* Video File */}
          <div style={styles.formGroup}>
            <label style={styles.label}>Video File *</label>
            <div style={styles.fileInput}>
              <input
                type="file"
                accept="video/mp4,video/avi,video/mov,video/mkv,video/*"
                onChange={(e) => setFile(e.target.files[0])}
                required
                style={{ display: 'none' }}
                id="video-file"
              />
              <label htmlFor="video-file" style={styles.fileLabel}>
                {file ? (
                  <>
                    <span>📹 {file.name}</span>
                    <span style={styles.fileSize}>
                      ({(file.size / (1024 * 1024)).toFixed(1)} MB)
                    </span>
                  </>
                ) : (
                  <>
                    <span style={{ fontSize: '24px' }}>📁</span>
                    <span>Click to select video file</span>
                    <span style={styles.fileHint}>MP4, AVI, MOV, MKV</span>
                  </>
                )}
              </label>
            </div>
          </div>

          {/* Progress Bar */}
          {loading && (uploadProgress > 0 || processingPhase) && (
            <div style={styles.progressContainer}>
              <div style={styles.progressBar}>
                <div
                  style={{
                    ...styles.progressFill,
                    width: `${processingPhase === 'uploading' ? uploadProgress : (processingPhase === 'processing' ? processingProgress : 100)}%`
                  }}
                />
              </div>
              <span style={styles.progressText}>
                {processingPhase === 'uploading' && `📤 Uploading: ${uploadProgress}%`}
                {processingPhase === 'processing' && `⚙️ GPU Processing on Modal: ${Math.round(processingProgress)}%`}
                {processingPhase === 'completed' && `✅ Complete!`}
              </span>
            </div>
          )}

          {/* Submit Button */}
          <button
            type="submit"
            style={{
              ...styles.primaryButton,
              opacity: loading ? 0.7 : 1,
              cursor: loading ? 'not-allowed' : 'pointer'
            }}
            disabled={loading}
          >
            {loading ? (
              processingPhase === 'uploading'
                ? `📤 Uploading... ${uploadProgress}%`
                : processingPhase === 'processing'
                ? `⚙️ GPU Processing... ${Math.round(processingProgress)}%`
                : 'Processing...'
            ) : (
              '🚀 Upload & Analyze'
            )}
          </button>
        </form>

        {/* Info Section */}
        <div style={styles.infoBox}>
          <h3 style={styles.infoTitle}>🎯 What happens next?</h3>
          <ol style={styles.infoList}>
            <li>Video uploads to our servers</li>
            <li>🚀 <strong>GPU Processing on Modal.com</strong> - Lightning-fast YOLO inference</li>
            <li>Detects: <strong>serves, spikes, blocks, sets, digs</strong></li>
            <li>Tracks players and jersey numbers automatically</li>
            <li>Generates statistics and coaching insights</li>
          </ol>
          <p style={styles.infoNote}>
            ⚡ Processing time: ~20-30% faster with GPU acceleration! (3.5-4.5x speedup)
          </p>
        </div>
      </div>
    </div>
  );
}

const styles = {
  container: {
    maxWidth: '700px',
    margin: '0 auto',
    padding: '40px 20px',
  },
  title: {
    fontSize: '28px',
    fontWeight: 'bold',
    color: '#1e3a8a',
    marginBottom: '24px',
  },
  card: {
    background: 'white',
    borderRadius: '12px',
    padding: '32px',
    boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
  },
  formGroup: {
    marginBottom: '20px',
    flex: 1,
  },
  label: {
    display: 'block',
    fontSize: '14px',
    fontWeight: '600',
    color: '#374151',
    marginBottom: '8px',
  },
  input: {
    width: '100%',
    padding: '12px',
    fontSize: '16px',
    border: '2px solid #e5e7eb',
    borderRadius: '8px',
    boxSizing: 'border-box',
    transition: 'border-color 0.2s',
  },
  select: {
    width: '100%',
    padding: '12px',
    fontSize: '16px',
    border: '2px solid #e5e7eb',
    borderRadius: '8px',
    boxSizing: 'border-box',
    background: 'white',
  },
  row: {
    display: 'flex',
    gap: '16px',
  },
  fileInput: {
    marginTop: '8px',
  },
  fileLabel: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '32px',
    border: '2px dashed #d1d5db',
    borderRadius: '12px',
    cursor: 'pointer',
    transition: 'all 0.2s',
    gap: '8px',
  },
  fileSize: {
    fontSize: '14px',
    color: '#6b7280',
  },
  fileHint: {
    fontSize: '12px',
    color: '#9ca3af',
  },
  progressContainer: {
    marginBottom: '20px',
  },
  progressBar: {
    height: '8px',
    background: '#e5e7eb',
    borderRadius: '4px',
    overflow: 'hidden',
  },
  progressFill: {
    height: '100%',
    background: 'linear-gradient(90deg, #667eea 0%, #764ba2 100%)',
    transition: 'width 0.3s',
  },
  progressText: {
    fontSize: '14px',
    color: '#6b7280',
    marginTop: '8px',
    display: 'block',
  },
  primaryButton: {
    width: '100%',
    padding: '16px',
    fontSize: '18px',
    fontWeight: '600',
    color: 'white',
    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
    border: 'none',
    borderRadius: '8px',
    cursor: 'pointer',
    marginTop: '16px',
  },
  infoBox: {
    marginTop: '32px',
    padding: '24px',
    background: '#f0f9ff',
    borderRadius: '12px',
    borderLeft: '4px solid #3b82f6',
  },
  infoTitle: {
    fontSize: '18px',
    fontWeight: '600',
    color: '#1e40af',
    marginBottom: '16px',
  },
  infoList: {
    paddingLeft: '20px',
    lineHeight: '1.8',
    color: '#1e40af',
  },
  infoNote: {
    marginTop: '16px',
    fontSize: '14px',
    color: '#6b7280',
    fontStyle: 'italic',
  },
  emptyState: {
    textAlign: 'center',
    padding: '48px 24px',
  },
};

export default UploadPage;
